import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import portalApi from "../../api/portalClient";
import { useDraggable } from "../../hooks/useDraggable";
import AILauncherButton from "../../components/AILauncherButton";

type Msg = { role: "user" | "ai"; text: string; suggest_ticket?: any };

export default function PortalAIChat() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { panelRef, onDragStart, dragStyle } = useDraggable();

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/ai/status")
      .then((r) => setEnabled(!!r.data.enabled))
      .catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await portalApi.post("/api/v1/portal/ai/chat", { message: text });
      setMsgs((m) => [
        ...m,
        {
          role: "ai",
          text: r.data.reply || "(no response)",
          suggest_ticket: r.data.suggest_ticket ? r.data : null,
        },
      ]);
    } catch (e: any) {
      setMsgs((m) => [
        ...m,
        {
          role: "ai",
          text:
            "Sorry — the assistant is having trouble: " +
            (e?.response?.data?.detail || e?.message || "unknown error"),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const openTicketFromSuggestion = async (s: any) => {
    try {
      const r = await portalApi.post("/api/v1/portal/tickets", {
        title: (s.ticket_title || "Help request from portal").slice(0, 200),
        description: s.ticket_description || "",
        priority: "medium",
      });
      navigate(`/portal/tickets/${r.data.id}`);
    } catch (e: any) {
      alert("Failed to open ticket: " + (e?.response?.data?.detail || e?.message));
    }
  };

  return (
    <>
      {!open && (
        <AILauncherButton
          onClick={() => setOpen(true)}
          title="Ask the AI assistant"
          icon="⚡"
          colorClass="bg-violet-600 hover:bg-violet-700"
          activeColorClass="bg-violet-700 ring-2 ring-violet-300"
        />
      )}
      {open && (
        <div
          ref={panelRef}
          style={dragStyle}
          className="fixed bottom-5 right-5 w-96 max-w-[95vw] h-[600px] max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col z-40 border border-gray-200 dark:border-gray-700"
        >
          <div
            onMouseDown={onDragStart}
            className="px-4 py-3 border-b flex items-center justify-between bg-violet-600 text-white rounded-t-xl cursor-move select-none"
          >
            <div className="font-semibold text-sm">⚡ AI Assistant</div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm bg-gray-50 dark:bg-gray-900/50">
            {msgs.length === 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                Hi! Ask me anything about your IT setup. I can walk you through known fixes from
                your runbooks or open a ticket for you if I can't help directly.
              </div>
            )}
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-lg px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-violet-600 text-white"
                      : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                  }`}
                >
                  {m.text}
                  {m.suggest_ticket && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="text-xs font-semibold mb-1">Open a ticket?</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <strong>Title:</strong>{" "}
                        {m.suggest_ticket.ticket_title || "(no title)"}
                      </div>
                      <button
                        onClick={() => openTicketFromSuggestion(m.suggest_ticket)}
                        className="text-xs bg-violet-600 hover:bg-violet-700 text-white rounded px-2 py-1"
                      >
                        Create ticket
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-gray-400 dark:text-gray-500">thinking…</div>}
          </div>
          <div className="border-t dark:border-gray-700 p-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Type your question…"
              className="w-full border rounded p-2 text-sm resize-none dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              disabled={busy}
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="text-sm px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded disabled:opacity-50"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
